import { ROUTE_TO_NEXT } from 'redux-router-kit';

import { getJson } from '@zapier/toolbox-browser-fetch';
import { urlWithNext } from '@zapier/url-utils';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';

import { isAuthRequiredForRoutes } from 'app/common/utils/RouterUtils';
import clearDismissables from 'app/developer-v3/utils/clearDismissables';

interface SessionData {
  is_logged_in: boolean;
  [key: string]: any;
}

// This class takes care of fetching and caching the user session.
// This is a rudimentary cache to prevent fetching the session on every route action that happens.
class SessionRepository {
  expires: number | null;
  session: SessionData | null;

  // The session endpoint doesn't give us a way to calculate when the session will expire,
  // nor can we access the session cookie since it came from zapier.com, so we use a rudimentary
  // cache expiration mechanism.
  // This should be okay because when the session expires and a random request later 401's, the
  // code in `autoPromiseMiddleware` should take care of redirecting the user back to zapier.com/app/login.
  static TTL = 1000 * 60 * 60; // 1 hour

  constructor() {
    this.expires = null;
    this.session = null;
  }

  async get(): Promise<SessionData> {
    // If there is a cached session and if the cache has not expired
    if (this.session && this.expires && this.expires > Date.now()) {
      return this.session;
    }

    this.expires = Date.now() + SessionRepository.TTL;
    this.session = await getJson('/api/v4/session/');
    return this.session;
  }
}

interface RouteAction {
  type: string;
  meta: {
    routeKey: string;
    url: string;
  };
}

interface AuthMiddlewareConfig {
  routes: any;
}

const createAuthenticationMiddleware = ({ routes }: AuthMiddlewareConfig) => {
  const session = new SessionRepository();

  const middleware = () => (next: (action: any) => any) => async (
    action: RouteAction
  ) => {
    if (!action || action.type !== ROUTE_TO_NEXT) {
      return next(action);
    }

    const { routeKey } = action.meta;
    const isAuthRequired = isAuthRequiredForRoutes(routes, routeKey);
    if (!isAuthRequired) {
      return next(action);
    }

    const { is_logged_in } = await session.get();

    if (is_logged_in) {
      return next(action);
    }

    clearDismissables('-missing-required-settings');

    // Redirec to login
    // You'll be redirected back automatically in staging or production once
    // logged in. Otherwise you'll need to do it manually since
    // https://zapier.com/app/login and https://zapier-staging.com/app/login
    // don't redirect to localhost.
    window.location = urlWithNext(
      `${ZAPIER_APP_BASE_URL}/app/login`,
      `${window.location.origin}${action.meta.url}` // action.meta.url will always starts with a slash
    ) as any;
    return Promise.resolve();
  };

  (middleware as any).setRoutes = (newRoutes: any) => {
    routes = newRoutes;
  };

  return middleware;
};

export default createAuthenticationMiddleware;
